import React from 'react'
import { reduxForm, SubmissionError, Field } from 'redux-form'
import LgyInputField from 'app/components/util/lgy-input-field/LgyInputField'
import LgyTextArea from 'app/components/util/lgy-text-area/LgyTextArea'
import IssueCategorySelect from 'app/gears/help/ticket/components/IssueCategorySelect'
import UserTypeSelect from 'app/gears/help/ticket/components/UserTypeSelect'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import { required, email, phoneNumber, maxLength2000, fullName } from 'app/utils/validations'
import LgyLoadingIcon from 'app/components/util/lgy-loading-icon/LgyLoadingIcon'
import LgyFormSubmit from 'app/components/util/lgy-form-submit/LgyFormSubmit'
import { catchCommonErrors, fetchPostingParams } from "app/actions/commonActionUtils"
import { convertLgyErrorResponseToFormErrorObject } from "app/utils/formUtils"
import '../style/lgy-help-ticket-form.scss'

/**
 * A form for users to submit help tickets if they are having some kind of issue.
 * 
 * @author Alex Clary
 * @author Matt Frazier
 * 
 * @param {*} user the current user 
 * @param {*} formValues the current values of the form
 * @param {*} reduxFormtthe props provided by the redux-form HOC
 */
const HelpTicketForm = ({ user, serverProperties, formValues, ...reduxForm }) => {
  const { submitSucceeded, submitting, handleSubmit } = reduxForm

  const submitHelpTicket = async (values, dispatch, props) => {
    const response = await fetch(`/lgyhub/api/tickets`, fetchPostingParams(values))
    const responseJson = await catchCommonErrors(dispatch, response, "submitting help ticket")
    if (response.status >= 400) {
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    }
  }

  return <div className='lgy-help-ticket-form usa-grid'>
    {submitting && <LgyLoadingIcon />}
    {submitSucceeded
      ? <LgyAlertDialog type='success' heading='Thanks for Your Help Request' >
          We will be in touch shortly. In the meantime, please check out our Helpful Links below to troubleshoot common issues:
          <ul>
            <li><a target='_blank' rel='noopener noreferrer' href="https://www.benefits.va.gov/homeloans/documents/docs/lgy_hub_user_guide.pdf">LGY Hub User Guide</a></li>
            <li><a target='_blank' rel='noopener noreferrer' href="https://lgy.va.gov/lgyhub/help">Frequently Asked Questions (FAQ) </a></li>
            <li>Registration video that walks through the registration process, including ID.me: <a target='_blank' rel='noopener noreferrer' href={serverProperties?.registrationVideoUrl}>Click here to view Registration Video</a></li>
          </ul>
          <br />
          Please do not submit multiple tickets for the same issue.
        </LgyAlertDialog>
      : <form onSubmit={handleSubmit(submitHelpTicket)} className='usa-form'>
      <fieldset>
        <div className='usa-grid'>
          <div className='usa-width-one-half'>
            <Field component={LgyInputField} label='Username' name='username' type='text' placeholder='Your VIP or FFPS username, if exists' />
            <Field component={LgyInputField} label='Name (First and Last)' name='firstName' type='text' validate={[required, fullName]} required />
            <Field component={LgyInputField} label='Phone Number' name='phoneNumber' type='text' validate={[required, phoneNumber]} required />
            <Field component={LgyInputField} label='Work Email' name='email' type='email' validate={[required, email]} required />
            {(() => {
              switch (formValues.userType) {
                case 'Appraiser':
                  return <Field label='Appraiser ID' name='appraiserId' component={LgyInputField} type='text' />
                case 'Inspector':
                  return <Field label='Inspector ID' name='inspectorId' component={LgyInputField} type='text' validate={required} required />
                case 'Lender':
                  return <div>
                    <Field label='Lender Name' name='lenderName' component={LgyInputField} type='text' validate={required} required />
                    <Field label='Lender ID' name='lenderId' component={LgyInputField} type='text' />
                  </div>
                case 'Other Requester':
                  return <Field label='Other Requester ID (Tax ID)' name='otherRequesterId' component={LgyInputField} type='text' />
                case 'SAR':
                  return <div>
                    <Field label='LAPP/SAPP SAR ID' name='sarId' component={LgyInputField} type='text' />
                  </div>
                case 'Servicer':
                  return <div>
                    <Field label='Servicer Name' name='servicerName' component={LgyInputField} type='text' validate={required} required />
                    <Field label='Servicer ID' name='servicerId' component={LgyInputField} type='text' />
                  </div>
                default:
                  return null;
              }
            })()}
          </div>
          <div className='usa-width-one-half'>
            <UserTypeSelect />
            <IssueCategorySelect formValues={formValues}/>
            <Field component={LgyTextArea} label='Issue Description' name='issueDescription' validate={[required, maxLength2000]} required 
              placeholder='Up to 2000 characters allowed. Please describe where you were and what you were doing when you encountered the issue. Please do not include full SSN, veteran information or bank account information to your help ticket.'/>
          </div>
        </div>

        <div className='usa-grid'>
          <LgyFormSubmit {...reduxForm} />
        </div>
      </fieldset>
    </form>}
  </div>
}

export default reduxForm({})(HelpTicketForm)
